<template>
  <IntegrationWorkflowWrapper
    v-model:is-enabled="isEnabled"
    title="Map Partner Data to CRM"
    description="Push partner-shared fields directly into your CRM. Enrich your Salesforce workflows, reports, and processes with unique data from your partners’ CRMs."
  >
    <!-- TODO: workflow status will live here -->
    <div class="flex flex-col gap-16 mt-16 mb-16">
      <PartnerFieldMapping
        v-for="(mapping, idx) in mappings"
        :key="mapping.id"
        :partner-fields="sharedPartnerFields"
        :crm-fields="getAvailableSourceFields(mapping.source_field_id)"
        :mapping="mapping"
        :index="idx"
        :disabled="!isEnabled"
        @changed:mapping="updateMapping"
        @deleted:mapping="deleteMapping"
      />
      <div class="flex justify-between items-center">
        <BittsButton
          text="Add field to map"
          variant="outline"
          size="small"
          :left-icon="['fak', 'add']"
          class="max-w-content"
          :disabled="!isEnabled"
          @click="
            () => {
              mappings.push({
                id: `mapping-${mappings.length}`,
                source_field_id: null,
                partner_organization_uuid: null,
                partner_source_field_id: null,
                empty: true,
              });
            }
          "
        />
        <BittsButton
          text="Save changes"
          size="small"
          :disabled="!isEnabled"
          class="max-w-content"
          @click="savePartnerFieldMappings"
        />
      </div>
    </div>
  </IntegrationWorkflowWrapper>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';

import { HttpResponse } from 'msw';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { enableMocks, typedHttp, worker } from '@/mocks';
import { useFeedsStore, usePartnersStore, useSourcesStore } from '@/stores';
import { IntegrationProfileType } from '@/types/integration_profiles';
import { Partner } from '@/types/partners';

import IntegrationWorkflowWrapper from './IntegrationWorkflowWrapper.vue';
import PartnerFieldMapping, { Mapping } from './PartnerFieldMapping.vue';

const { integrationProfileType } = defineProps<{
  integrationProfileType: IntegrationProfileType | undefined;
}>();

const hardeesUUID = '03cc3cbf-8e42-4648-80a4-77b45eb5566d';
const lucasFilmUUID = '03cc3cbe-48f4-40be-beb8-afb7c7319c9b';
const northDakotaStateUniversity = '03cc3cbe-3d89-4b1d-81d9-14dcfb5a6f6b';

const buildFakeFields = () => {
  const firstBatch = Array.from({ length: 10 }, (_, i) => ({
    nickname: `Source Field ${i}`,
    column: `source_field_${i}`,
    source_field_id: i,
    organization_uuid: hardeesUUID,
  }));

  const secondBatch = Array.from({ length: 10 }, (_, i) => ({
    nickname: `Source Field ${i + 10}`,
    column: `source_field_${i + 10}`,
    source_field_id: i + 10,
    organization_uuid: lucasFilmUUID,
  }));

  const thirdBatch = Array.from({ length: 10 }, (_, i) => ({
    nickname: `Source Field ${i + 20}`,
    column: `source_field_${i + 20}`,
    source_field_id: i + 20,
    organization_uuid: northDakotaStateUniversity,
  }));
  return [...thirdBatch, ...firstBatch, ...secondBatch];
};

type SharedPartnerField = {
  nickname: string;
  column: string;
  source_field_id: number;
  organization_uuid: string;
};

export interface PartnerOption extends Partner {
  label: string;
  value: number;
}

if (enableMocks) {
  worker.use(
    // @ts-expect-error endpoint not implemented yet
    typedHttp.get('/v0.1/partners/global/shared-fields', () =>
      HttpResponse.json({
        items: buildFakeFields(),
      }),
    ),
    typedHttp.get(
      // @ts-expect-error endpoint not implemented yet
      '/v0.1/integration-profiles/:profile_id/partner-field-mappings',
      () =>
        HttpResponse.json({
          items: [
            {
              id: 'foo',
              source_field_id: 339,
              partner_organization_uuid: northDakotaStateUniversity,
              partner_source_field_id: 21,
            },
            {
              id: 'bar',
              source_field_id: 353,
              partner_organization_uuid: hardeesUUID,
              partner_source_field_id: 8,
            },
          ],
        }),
    ),
    typedHttp.put(
      // @ts-expect-error endpoint not implemented yet
      '/v0.1/integration-profiles/:profile_id/partner-field-mappings',
      () => HttpResponse.json({}),
    ),
  );
}
const isEnabled = defineModel<boolean>('isEnabled');

const sharedPartnerFields = ref<PartnerOption[]>([]);
const mappings = ref<Mapping[]>([]);

const partnersStore = usePartnersStore();
const sourcesStore = useSourcesStore();
const feedsStore = useFeedsStore();

const { crmFeeds } = storeToRefs(feedsStore);

const integrationCRM = computed(() => {
  return crmFeeds.value.find(
    (feed) => feed.integration.type === integrationProfileType,
  );
});

const integrationCRMSources = computed(() => {
  const accountSource = sourcesStore
    .getSourcesByMdmType('account')
    ?.find(
      (source) =>
        source.feed_id === integrationCRM.value?.id &&
        source.mdm_type === 'account',
    );

  if (!accountSource) return [];

  return accountSource?.fields
    .filter((f) => f.data_type === 'string')
    .map((f) => ({
      label: f.nickname as string,
      value: f.id as number,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const mappedSourceFieldIds = computed(
  () =>
    new Set(
      mappings.value
        .filter((m) => m.source_field_id !== null)
        .map((m) => m.source_field_id),
    ),
);

const getAvailableSourceFields = (currentId: number | null) =>
  integrationCRMSources.value.filter(
    (field) =>
      field.value === currentId || !mappedSourceFieldIds.value.has(field.value),
  );

function deleteMapping(index: number) {
  if (mappings.value.length === 1) {
    mappings.value[0] = {
      id: 'foo',
      source_field_id: null,
      partner_organization_uuid: null,
      partner_source_field_id: null,
      empty: true,
    };
    return;
  }
  const mappingToDelete = mappings.value[index];
  if (!mappingToDelete) return;
  mappings.value.splice(index, 1);
}

function updateMapping(payload: { mapping: Mapping; index: number }) {
  const { mapping, index } = payload;
  const existingMapping = mappings.value[index];
  if (!existingMapping) return;
  mappings.value[index] = mapping;
}

const mapPartnersToFields = (field: SharedPartnerField) => {
  const { organization_uuid, nickname, source_field_id } = field;
  const partner = partnersStore.getPartnerOrgByUuid(organization_uuid);
  if (!partner) {
    return null;
  }
  return {
    ...partner,
    users: partner.users ?? [],
    tags: partner.tags ?? [],
    label: nickname,
    value: source_field_id,
  };
};

async function savePartnerFieldMappings() {
  const body = mappings.value
    .filter(
      (m) => m.source_field_id !== null && m.partner_source_field_id !== null,
    )
    .map((m) => ({
      source_field_id: m.source_field_id,
      partner_source_field_id: m.partner_source_field_id,
    }));
  await crossbeamApi.PUT(
    // @ts-expect-error route not implemented yet
    '/v0.1/integration-profiles/:profile_id/partner-field-mappings',
    {
      body,
    },
  );
}

onBeforeMount(async () => {
  if (!enableMocks) return;

  const [sharedResponse, mappingResponse] = (await Promise.all([
    // @ts-expect-error endpoints not implemented yet
    crossbeamApi.GET('/v0.1/partners/global/shared-fields'),
    // @ts-expect-error endpoints not implemented yet
    crossbeamApi.GET(
      `/v0.1/integration-profiles/${integrationCRM.value?.id}/partner-field-mappings`,
    ),
  ])) as [
    { data: { items: SharedPartnerField[] } },
    { data?: { items: Mapping[] } },
  ];

  if (sharedResponse.data) {
    sharedPartnerFields.value = sharedResponse.data.items
      .reduce((acc, curr) => {
        const mappedField = mapPartnersToFields(curr);
        if (mappedField) acc.push(mappedField);
        return acc;
      }, [] as PartnerOption[])
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  if (mappingResponse.data) {
    mappings.value = mappingResponse.data.items;
  }
});
</script>
